import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DialogLinkuRecenzji } from "../components/recenzje/DialogLinkuRecenzji.js";
import { KartaLinkow } from "../components/recenzje/KartaLinkow.js";
import { KartaPoprawek } from "../components/recenzje/KartaPoprawek.js";
import { Banner } from "../components/ui/Banner.js";
import { Button } from "../components/ui/Button.js";
import { SegmentedControl } from "../components/ui/SegmentedControl.js";
import { useZasob } from "../hooks/useZasob.js";
import { api } from "../lib/api.js";

/**
 * Panel "Recenzje" (tylko administrator): linki dla zewnetrznych recenzentow i ich poprawki do oceny modelu.
 *
 * Dwa podwidoki w przelaczniku (SG-3), zapamietane w adresie (`?karta=poprawki`), zeby odswiezenie nie gubilo miejsca.
 * Liczby przy nazwach podwidokow pokazujemy dopiero po pobraniu danych: "Linki (0)" przed odpowiedzia byloby nieprawda.
 *
 * Aktywny podwidok odswieza sie sam co 15 s (recenzent pracuje w tle: rosnie postep i przybywa poprawek). Nieudane ciche
 * odswiezenie zostawia dane na ekranie, a nad kartami staje jeden baner stanu (BN-1, BN-3).
 */

const ODSWIEZANIE_MS = 15_000;

const KARTY = [
  { wartosc: "linki", etykieta: "Linki" },
  { wartosc: "poprawki", etykieta: "Poprawki" },
] as const;
type Karta = (typeof KARTY)[number]["wartosc"];

function odczytajKarte(wartosc: string | null): Karta {
  return KARTY.find((k) => k.wartosc === wartosc)?.wartosc ?? "linki";
}

export function RecenzjePage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const karta = odczytajKarte(params.get("karta"));
  const [oknoLinku, setOknoLinku] = useState(false);

  const linki = useZasob("recenzje-linki", () => api.recenzjeLinki(), { co: karta === "linki" ? ODSWIEZANIE_MS : undefined });
  const poprawki = useZasob("recenzje-poprawki", () => api.recenzjePoprawki(), {
    co: karta === "poprawki" ? ODSWIEZANIE_MS : undefined,
  });

  const aktywny = karta === "linki" ? linki : poprawki;
  const bladOdswiezania = aktywny.blad !== null && aktywny.dane !== null;

  const opcje = KARTY.map((k) => {
    const dane = k.wartosc === "linki" ? linki.dane : poprawki.dane;
    return { wartosc: k.wartosc, etykieta: dane ? `${k.etykieta} (${dane.length})` : k.etykieta };
  });

  return (
    <>
      <h1 className="page-title">Recenzje</h1>
      <p className="page-lead">Linki dla ekspertów i ich poprawki do oceny.</p>

      <div className="recenzje-karty">
        <SegmentedControl
          etykieta="Widok"
          opcje={opcje}
          wartosc={karta}
          onZmiana={(k) => setParams(k === "linki" ? new URLSearchParams() : new URLSearchParams({ karta: k }), { replace: true })}
        />
      </div>

      {bladOdswiezania && (
        <Banner
          wariant="warning"
          tekst="Nie udaje się odświeżyć danych. Pokazujemy ostatnie, jakie mamy."
          akcja={
            <Button wariant="secondary" onClick={aktywny.odswiez}>
              Odśwież
            </Button>
          }
        />
      )}

      {karta === "linki" ? (
        <KartaLinkow zasob={linki} onNowyLink={() => setOknoLinku(true)} />
      ) : (
        <KartaPoprawek zasob={poprawki} />
      )}

      <DialogLinkuRecenzji
        otwarty={oknoLinku}
        onZamknij={() => setOknoLinku(false)}
        onUtworzono={() => linki.odswiez()}
      />
    </>
  );
}
